import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

// Importing custom components
import FundApiComponent from '../../components/FundApiComponents'
import HorizontalTabs from '../../components/Tabs/HorizontalTabs'
import Footnotes from '../../components/Footnotes/FundClassLegalText'
import Accordion from '../../components/Common/Accordion'

import { useSideNavigationContext } from '../../utils/context/sideNavigationContext'
import { mapApiComponent } from './utils'

// Importing color variables
import { BLUE_BASE } from '../../theme/variables/colors'

// Styled Components to replicate original styling
const StyledTitle = styled(Typography)(({ theme }) => ({
  margin: '40px 0',
  paddingBottom: '24px',
  borderBottom: `3px solid ${BLUE_BASE}`,
  width: 'fit-content',
  [theme.breakpoints.down('sm')]: {
    margin: '10px 0',
  },
}))

// Render content for each tab
const renderTabContent = (
  tab,
  fundClass,
  pageContext,
  title,
  productType,
  fundTitle
) => {
  const api = get(tab, 'api_reference[0]', {})
  api.fundTitle = fundTitle
  api.component = mapApiComponent(api)

  return (
    <Box key={api?.id} label={tab.title} component="div" role="tabpanel">
      <FundApiComponent
        api={api}
        fundClass={fundClass}
        ticker={pageContext.ticker}
        title={title}
        productType={productType}
      />
    </Box>
  )
}

// Render horizontal tabs if multiple tabs exist
const horizontalTabsRenderer = (
  section,
  pageContext,
  fundClass,
  title,
  productType,
  fundTitle
) => (
  <HorizontalTabs>
    {section.section_tabs?.map((tab) =>
      renderTabContent(
        tab,
        fundClass,
        pageContext,
        title,
        productType,
        fundTitle
      )
    )}
  </HorizontalTabs>
)

// Render the main section content, either a single accordion or accordion with tabs
const renderSectionContent = (
  section,
  fundClass,
  pageContext,
  productType,
  sectionIndex,
  fundTitle
) => {
  const api = get(section, 'api_reference[0]', {})
  api.fundTitle = fundTitle
  api.component = mapApiComponent(api)

  // If only one tab or no tabs, just render a single accordion with content
  if (!section.section_tabs || section.section_tabs.length <= 1) {
    return (
      <Accordion
        key={api?.id}
        title={section.title}
        hidden={section.default_hidden}
        type="performance"
      >
        <Box>
          <FundApiComponent
            api={api}
            fundClass={fundClass}
            ticker={pageContext.ticker}
            title={section.title}
            productType={productType}
          />
          <Footnotes
            content={section.footnotes}
            activeShareClass={pageContext.fundClass}
          />
        </Box>
      </Accordion>
    )
  }

  // If multiple tabs, render accordion with horizontal tabs inside
  return (
    <Accordion
      key={`expand-section-api-${sectionIndex}`}
      title={section.title}
      hidden={section.default_hidden}
      type="performance"
    >
      {horizontalTabsRenderer(
        section,
        pageContext,
        fundClass,
        section.title,
        productType,
        fundTitle
      )}
      <Footnotes
        content={section.footnotes}
        activeShareClass={pageContext.fundClass}
      />
    </Accordion>
  )
}

const FundPerformance = ({
  section_title,
  data_section,
  pageContext,
  id,
  fund_classes,
  productType,
  index,
  fundTitle,
}) => {
  const { createSectionRef } = useSideNavigationContext()

  // Find the current fund class based on pageContext
  const fundClass = fund_classes?.find(
    (item) => item?.share_class?.[0]?.title === pageContext.fundClass?.title
  )

  return (
    <section ref={createSectionRef(index)}>
      <StyledTitle id={id} variant="h2">
        {section_title}
      </StyledTitle>

      {data_section?.section?.map((section, sectionIndex) =>
        renderSectionContent(
          section,
          fundClass,
          pageContext,
          productType,
          sectionIndex,
          fundTitle
        )
      )}
    </section>
  )
}

FundPerformance.propTypes = {
  section_title: PropTypes.string,
  data_section: PropTypes.any,
  pageContext: PropTypes.any,
  id: PropTypes.string,
  fund_classes: PropTypes.any,
  productType: PropTypes.string,
  index: PropTypes.number,
  fundTitle: PropTypes.string,
}

export default FundPerformance